export type LiveConflictField =
  | 'deptC'
  | 'deptP'
  | 'jz'
  | 'pm'
  | 'lh'
  | 'yc'
  | 'cause'
  | 'temp'
  | 'permanent'
  | 'fzr'
  | 'planDate'
  | 'replyActual'
  | 'defectActual'
  | 'closeActual';

export interface LiveConflictValue {
  system: string;
  excel: string;
}

export interface LiveCase {
  id: string;
  year: string | null;
  sourceRow: number | null;
  matchKey: string | null;
  date: string;
  deptC: string | null;
  deptP: string;
  jz: string | null;
  pm: string | null;
  lh: string | null;
  yc: string;
  cause: string | null;
  temp: string | null;
  permanent: string | null;
  fzr: string;
  planDate: string;
  replyPlanDate: string | null;
  replyActual: string | null;
  defectActual: string | null;
  closeActual: string | null;
  conflicts: Partial<Record<LiveConflictField, LiveConflictValue>>;
}

export interface LiveCasePatch {
  date?: string;
  deptC?: string | null;
  deptP?: string;
  jz?: string | null;
  pm?: string | null;
  lh?: string | null;
  yc?: string;
  cause?: string | null;
  temp?: string | null;
  permanent?: string | null;
  fzr?: string;
  planDate?: string;
  replyPlanDate?: string | null;
  replyActual?: string | null;
  defectActual?: string | null;
}

export interface LiveRate {
  filled: number;
  total: number;
  percent: number | null;
}

export interface LiveSummary {
  unreplied: number;
  handling: number;
  defectOpen: number;
  replyOntime: LiveRate;
  defectOntime: LiveRate;
  closeOntime: LiveRate;
}

export interface ExcelLiveRow {
  sourceRow: number;
  date: string;
  deptC: string | null;
  deptP: string;
  jz: string | null;
  pm: string | null;
  lh: string | null;
  yc: string | null;
  cause: string | null;
  temp: string | null;
  permanent: string | null;
  fzr: string | null;
  replyActual: string | null;
  defectActual: string | null;
  closeActual: string | null;
  closePlan: string | null;
}

const MERGE_FIELDS: LiveConflictField[] = [
  'deptC',
  'deptP',
  'jz',
  'pm',
  'lh',
  'yc',
  'cause',
  'temp',
  'permanent',
  'fzr',
  'planDate',
  'replyActual',
  'defectActual',
  'closeActual',
];

function trim(value: string | null | undefined): string {
  return (value ?? '').trim();
}

export function textFilled(value: string | null | undefined): boolean {
  return trim(value).length > 0;
}

export function ymd(value: string | null | undefined): string | null {
  if (!value) return null;
  const matched = /^(\d{4}-\d{2}-\d{2})/.exec(String(value).trim());
  return matched ? matched[1] : null;
}

export function addCalendarDays(dateYmd: string, days: number): string {
  const base = Date.parse(`${dateYmd}T00:00:00Z`);
  return new Date(base + days * 86400000).toISOString().slice(0, 10);
}

/** 回复/不良品及时率：投诉日起算 */
export const LIVE_REPLY_PLAN_DAYS = 3;
/** 结案计划日默认：投诉日起算（可被行内 planDate 覆盖） */
export const LIVE_CLOSE_PLAN_DAYS = 7;

export function liveReplyPlanDate(dateYmd: string | null | undefined): string {
  const base = ymd(dateYmd);
  return base ? addCalendarDays(base, LIVE_REPLY_PLAN_DAYS) : '';
}

export function liveReplyPlanDateForRow(row: {
  date: string;
  replyPlanDate?: string | null;
}): string {
  const manual = trim(row.replyPlanDate);
  if (manual) return manual;
  return liveReplyPlanDate(row.date);
}

export function liveClosePlanDateForRow(row: {
  date: string;
  planDate?: string | null;
}): string {
  const manual = trim(row.planDate);
  if (manual) return manual;
  const base = ymd(row.date);
  return base ? addCalendarDays(base, LIVE_CLOSE_PLAN_DAYS) : '';
}

function dateFilled(value: string | null | undefined): boolean {
  return ymd(value) != null || trim(value) === '*';
}

function normField(value: string | null | undefined): string {
  const text = trim(value);
  return text === '' ? '' : text;
}

function compareField(system: string | null | undefined, excel: string | null | undefined): boolean {
  return normField(system) === normField(excel);
}

export function matchKey(
  year: string,
  row: { date: string; deptP: string; lh?: string | null; pm?: string | null; yc?: string | null },
): string {
  const part = trim(row.lh) || trim(row.pm) || '';
  return [year, trim(row.date), trim(row.deptP), part, trim(row.yc)].join('|');
}

export function liveLanes(row: LiveCase, today: string): {
  unreplied: boolean;
  handling: boolean;
  defectOpen: boolean;
  replyOverdue: boolean;
  closeOverdue: boolean;
} {
  const closed = dateFilled(row.closeActual);
  const replied =
    textFilled(row.cause) || textFilled(row.temp) || dateFilled(row.replyActual);
  const replyDeadline = liveReplyPlanDateForRow(row);
  const unreplied = !closed && !replied;
  const handling = !closed && replied;
  const defectOpen = !closed && !dateFilled(row.defectActual);
  const replyOverdue = unreplied && today > replyDeadline;
  const closeOverdue = !closed && replied && today > row.planDate;
  return { unreplied, handling, defectOpen, replyOverdue, closeOverdue };
}

function rate(filled: number, total: number): LiveRate {
  if (total === 0) return { filled: 0, total: 0, percent: null };
  return {
    filled,
    total,
    percent: Math.round((filled / total) * 1000) / 10,
  };
}

export function summarizeLive(rows: LiveCase[]): LiveSummary {
  let unreplied = 0;
  let handling = 0;
  let defectOpen = 0;
  const today = new Date().toISOString().slice(0, 10);
  rows.forEach((row) => {
    const lanes = liveLanes(row, today);
    if (lanes.unreplied) unreplied += 1;
    if (lanes.handling) handling += 1;
    if (lanes.defectOpen) defectOpen += 1;
  });

  const replyDeadlineOf = (row: LiveCase) => liveReplyPlanDateForRow(row);
  const replied = rows.filter((row) => dateFilled(row.replyActual));
  const replyOntimeCount = replied.filter(
    (row) => (ymd(row.replyActual) as string) <= replyDeadlineOf(row),
  ).length;

  const defectDone = rows.filter((row) => dateFilled(row.defectActual));
  const defectOntimeCount = defectDone.filter(
    (row) => (ymd(row.defectActual) as string) <= replyDeadlineOf(row),
  ).length;

  const closed = rows.filter((row) => dateFilled(row.closeActual));
  const closeOntimeCount = closed.filter(
    (row) => (ymd(row.closeActual) as string) <= row.planDate,
  ).length;

  return {
    unreplied,
    handling,
    defectOpen,
    replyOntime: rate(replyOntimeCount, replied.length),
    defectOntime: rate(defectOntimeCount, defectDone.length),
    closeOntime: rate(closeOntimeCount, closed.length),
  };
}

export function applyLiveEdit(row: LiveCase, patch: LiveCasePatch, today: string): LiveCase {
  const next: LiveCase = { ...row, conflicts: { ...row.conflicts } };
  const frozen = dateFilled(row.closeActual);
  const allowed: LiveCasePatch = { ...patch };
  if (frozen) {
    delete allowed.planDate;
    delete allowed.replyPlanDate;
    delete allowed.date;
    delete allowed.replyActual;
  }

  (Object.keys(allowed) as (keyof LiveCasePatch)[]).forEach((key) => {
    const value = allowed[key];
    if (value === undefined) return;
    if (key === 'date') {
      const parsed = ymd(value == null ? '' : String(value));
      if (parsed) next.date = parsed;
      return;
    }
    if (key === 'deptP' || key === 'fzr' || key === 'yc') {
      (next as unknown as Record<string, unknown>)[key] =
        value == null ? '' : String(value).trim();
      return;
    }
    (next as unknown as Record<string, unknown>)[key] =
      value == null || String(value).trim() === '' ? null : String(value).trim();
  });

  const replyActualInPatch = Object.prototype.hasOwnProperty.call(allowed, 'replyActual');
  if (
    !replyActualInPatch &&
    !dateFilled(next.replyActual) &&
    (textFilled(next.cause) || textFilled(next.temp))
  ) {
    next.replyActual = today;
  }
  return next;
}

export function markDefect(row: LiveCase, today: string): LiveCase {
  if (dateFilled(row.closeActual)) return row;
  return { ...row, defectActual: today, conflicts: { ...row.conflicts } };
}

export function undoDefect(row: LiveCase): LiveCase {
  if (dateFilled(row.closeActual)) return row;
  return { ...row, defectActual: null, conflicts: { ...row.conflicts } };
}

export function closeCase(row: LiveCase, today: string): LiveCase {
  if (dateFilled(row.closeActual)) return row;
  return { ...row, closeActual: today, conflicts: { ...row.conflicts } };
}

export function validateCreate(input: {
  yc?: string;
  deptP?: string;
  fzr?: string;
  planDate?: string;
  date: string;
}): { yc: string; deptP: string; fzr: string; planDate: string } {
  const yc = trim(input.yc);
  const deptP = trim(input.deptP);
  const fzr = trim(input.fzr);
  const planDate = trim(input.planDate) || addCalendarDays(input.date, 7);
  if (!yc || !deptP || !fzr || !planDate) {
    throw new Error('异常投诉、生产部门、负责人、完成计划日不能为空');
  }
  return { yc, deptP, fzr, planDate };
}

export function liveCaseFromExcel(year: string, row: ExcelLiveRow, id: string): LiveCase {
  const planDate = ymd(row.closePlan) ?? addCalendarDays(row.date, 7);
  return {
    id,
    year,
    sourceRow: row.sourceRow,
    matchKey: matchKey(year, {
      date: row.date,
      deptP: row.deptP,
      lh: row.lh,
      pm: row.pm,
      yc: row.yc,
    }),
    date: row.date,
    deptC: row.deptC,
    deptP: row.deptP,
    jz: row.jz,
    pm: row.pm,
    lh: row.lh,
    yc: trim(row.yc) || '',
    cause: row.cause,
    temp: row.temp,
    permanent: row.permanent,
    fzr: trim(row.fzr) || '',
    planDate,
    replyPlanDate: null,
    replyActual: row.replyActual,
    defectActual: row.defectActual,
    closeActual: row.closeActual,
    conflicts: {},
  };
}

function excelPlanDate(row: ExcelLiveRow): string | null {
  return ymd(row.closePlan);
}

function systemField(row: LiveCase, field: LiveConflictField): string | null {
  return row[field] as string | null;
}

function excelField(row: ExcelLiveRow, field: LiveConflictField): string | null {
  if (field === 'planDate') return excelPlanDate(row);
  return row[field as keyof ExcelLiveRow] as string | null;
}

function mergeField(
  row: LiveCase,
  excel: ExcelLiveRow,
  field: LiveConflictField,
): void {
  const system = systemField(row, field);
  const incoming = excelField(excel, field);
  if (compareField(system, incoming)) return;
  if (!normField(system) && normField(incoming)) {
    (row as unknown as Record<string, unknown>)[field] =
      field === 'deptP' || field === 'fzr' || field === 'yc'
        ? trim(incoming)
        : trim(incoming) || null;
    if (field === 'planDate') {
      row.planDate = trim(incoming) || row.planDate;
    }
    delete row.conflicts[field];
    return;
  }
  if (normField(system) && !normField(incoming)) return;
  if (normField(system) && normField(incoming)) {
    row.conflicts[field] = { system: normField(system), excel: normField(incoming) };
  }
}

function mergePair(system: LiveCase, excel: ExcelLiveRow): LiveCase {
  const next: LiveCase = {
    ...system,
    sourceRow: excel.sourceRow,
    conflicts: { ...system.conflicts },
  };
  MERGE_FIELDS.forEach((field) => mergeField(next, excel, field));
  return next;
}

export function mergeLiveCases(
  existing: LiveCase[],
  incomingRows: ExcelLiveRow[],
  year: string,
): LiveCase[] {
  const systemOnly = existing.filter((row) => row.matchKey == null);
  const keyed = existing.filter((row) => row.matchKey != null);
  const byKey = new Map<string, LiveCase[]>();
  keyed.forEach((row) => {
    const key = row.matchKey as string;
    const list = byKey.get(key) ?? [];
    list.push(row);
    byKey.set(key, list);
  });
  Array.from(byKey.values()).forEach((list) => {
    list.sort((a: LiveCase, b: LiveCase) => (a.sourceRow ?? 0) - (b.sourceRow ?? 0));
  });

  const incomingByKey = new Map<string, ExcelLiveRow[]>();
  incomingRows.forEach((row) => {
    const key = matchKey(year, {
      date: row.date,
      deptP: row.deptP,
      lh: row.lh,
      pm: row.pm,
      yc: row.yc,
    });
    const list = incomingByKey.get(key) ?? [];
    list.push(row);
    incomingByKey.set(key, list);
  });
  incomingByKey.forEach((list) => {
    list.sort((a, b) => a.sourceRow - b.sourceRow);
  });

  const usedKeys = new Set<string>();
  const merged: LiveCase[] = [...systemOnly];

  incomingByKey.forEach((excelList, key) => {
    usedKeys.add(key);
    const systemList = byKey.get(key) ?? [];
    const pairCount = Math.min(systemList.length, excelList.length);
    for (let i = 0; i < pairCount; i += 1) {
      merged.push(mergePair(systemList[i], excelList[i]));
    }
    for (let i = pairCount; i < systemList.length; i += 1) {
      merged.push(systemList[i]);
    }
    for (let i = pairCount; i < excelList.length; i += 1) {
      merged.push(liveCaseFromExcel(year, excelList[i], `import-${year}-${excelList[i].sourceRow}`));
    }
  });

  keyed.forEach((row) => {
    const key = row.matchKey as string;
    if (!usedKeys.has(key)) merged.push(row);
  });

  return merged.sort((a, b) => {
    const dateCmp = a.date.localeCompare(b.date);
    if (dateCmp !== 0) return dateCmp;
    return (a.sourceRow ?? 0) - (b.sourceRow ?? 0);
  });
}

export function resolveConflict(
  row: LiveCase,
  field: LiveConflictField,
  side: 'system' | 'excel',
): LiveCase {
  const conflict = row.conflicts[field];
  if (!conflict) return row;
  const next: LiveCase = { ...row, conflicts: { ...row.conflicts } };
  if (side === 'excel') {
    const value = conflict.excel;
    switch (field) {
      case 'planDate':
        next.planDate = value;
        break;
      case 'deptP':
        next.deptP = value;
        break;
      case 'fzr':
        next.fzr = value;
        break;
      case 'yc':
        next.yc = value;
        break;
      case 'deptC':
        next.deptC = value || null;
        break;
      case 'jz':
        next.jz = value || null;
        break;
      case 'pm':
        next.pm = value || null;
        break;
      case 'lh':
        next.lh = value || null;
        break;
      case 'cause':
        next.cause = value || null;
        break;
      case 'temp':
        next.temp = value || null;
        break;
      case 'permanent':
        next.permanent = value || null;
        break;
      case 'replyActual':
        next.replyActual = value || null;
        break;
      case 'defectActual':
        next.defectActual = value || null;
        break;
      case 'closeActual':
        next.closeActual = value || null;
        break;
      default:
        break;
    }
  }
  delete next.conflicts[field];
  return next;
}

export function hasConflicts(row: LiveCase): boolean {
  return Object.keys(row.conflicts).length > 0;
}
